/**
 * SVG Import utilities
 * Parse uploaded SVG files into TemplateShapes
 */
import type { TemplateShape } from '../types/dbTypes';

/**
 * Parse SVG transform attribute to extract translation values
 * Supports matrix(a,b,c,d,tx,ty) and translate(tx,ty) formats
 */
function parseTransform(transform: string | null): { tx: number; ty: number } {
    if (!transform) return { tx: 0, ty: 0 };

    // matrix(a,b,c,d,tx,ty) - translation is in positions 5 and 6
    const matrixMatch = transform.match(/matrix\s*\(\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^,]+),\s*([^)]+)\)/);
    if (matrixMatch) {
        return { tx: parseFloat(matrixMatch[5]), ty: parseFloat(matrixMatch[6]) };
    }

    // translate(tx, ty) or translate(tx)
    const translateMatch = transform.match(/translate\s*\(\s*([^,)]+)(?:,\s*([^)]+))?\)/);
    if (translateMatch) {
        return { tx: parseFloat(translateMatch[1]), ty: parseFloat(translateMatch[2] || '0') };
    }

    return { tx: 0, ty: 0 };
}

export interface ImportResult {
    shapes: Omit<TemplateShape, 'id' | 'templateId'>[];
    needsScaling: boolean;
    originalSize: { width: number; height: number };
    svgContent: string;
}

/**
 * Calculate the actual bounding box of SVG content by rendering it
 * This gives us the true visual bounds, not just the viewBox
 */
async function calculateSvgBounds(svgContent: string): Promise<{
    x: number;
    y: number;
    width: number;
    height: number;
    viewBoxWidth: number;
    viewBoxHeight: number;
}> {
    return new Promise((resolve) => {
        // Create a temporary container
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '-9999px';
        container.style.visibility = 'hidden';
        document.body.appendChild(container);

        // Parse and insert the SVG
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svg = doc.querySelector('svg');

        if (!svg) {
            document.body.removeChild(container);
            resolve({ x: 0, y: 0, width: 100, height: 100, viewBoxWidth: 100, viewBoxHeight: 100 });
            return;
        }

        // Get viewBox dimensions
        let viewBoxWidth = 100;
        let viewBoxHeight = 100;
        const viewBox = svg.getAttribute('viewBox');
        if (viewBox) {
            const parts = viewBox.split(/[\s,]+/).map(Number);
            if (parts.length === 4) {
                viewBoxWidth = parts[2];
                viewBoxHeight = parts[3];
            }
        } else {
            const widthAttr = svg.getAttribute('width');
            const heightAttr = svg.getAttribute('height');
            if (widthAttr) viewBoxWidth = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || viewBoxWidth;
            if (heightAttr) viewBoxHeight = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || viewBoxHeight;
        }

        // Import the SVG node into the document
        const importedSvg = document.importNode(svg, true) as SVGSVGElement;
        container.appendChild(importedSvg);

        // Use requestAnimationFrame to ensure the SVG is rendered
        requestAnimationFrame(() => {
            try {
                // Remove invisible elements before calculating bounds
                // These are elements with fill:none and no stroke that extend the bbox
                const allElements = importedSvg.querySelectorAll('path, circle, ellipse, polygon, polyline, line, rect');
                allElements.forEach(el => {
                    const style = window.getComputedStyle(el);
                    const fill = style.fill;
                    const stroke = style.stroke;
                    // Remove if fill is none and stroke is none/transparent
                    if ((fill === 'none' || fill === 'transparent') &&
                        (stroke === 'none' || stroke === 'transparent' || stroke === '')) {
                        el.remove();
                    }
                });

                // Get the bounding box of visible content only
                const bbox = importedSvg.getBBox();

                // Clean up
                document.body.removeChild(container);

                resolve({
                    x: bbox.x,
                    y: bbox.y,
                    width: bbox.width,
                    height: bbox.height,
                    viewBoxWidth,
                    viewBoxHeight
                });
            } catch {
                // Fallback if getBBox fails
                document.body.removeChild(container);
                resolve({
                    x: 0,
                    y: 0,
                    width: viewBoxWidth,
                    height: viewBoxHeight,
                    viewBoxWidth,
                    viewBoxHeight
                });
            }
        });
    });
}

/**
 * Crop SVG to its actual content bounds by adjusting the viewBox
 */
function cropSvgToContent(svgContent: string, bounds: { x: number; y: number; width: number; height: number }): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svg = doc.querySelector('svg');

    if (!svg) return svgContent;

    // Add small padding (1 unit)
    const padding = 1;
    const newViewBox = `${bounds.x - padding} ${bounds.y - padding} ${bounds.width + padding * 2} ${bounds.height + padding * 2}`;

    svg.setAttribute('viewBox', newViewBox);
    svg.setAttribute('width', `${bounds.width + padding * 2}`);
    svg.setAttribute('height', `${bounds.height + padding * 2}`);

    // Serialize back to string
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svg);
}

/**
 * Parse an SVG file and extract shapes (async version)
 *
 * Logic:
 * - If SVG has graphic content (paths, etc.) = treat whole SVG as one graphic
 * - Ungrouped rects = input field placeholders or borders
 * - We crop the SVG to its actual content bounds (removes empty space)
 * - We always keep the full SVG to preserve styles, defs, and CSS classes
 */
export async function parseSvgFileAsync(
    svgContent: string,
    workAreaWidth: number,
    workAreaHeight: number
): Promise<ImportResult> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svg = doc.querySelector('svg');

    if (!svg) {
        throw new Error('Invalid SVG file');
    }

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
        throw new Error('Invalid SVG file: ' + parseError.textContent);
    }

    const shapes: Omit<TemplateShape, 'id' | 'templateId'>[] = [];
    let orderIndex = 0;

    // Check if SVG has graphic content (paths, shapes, etc.)
    const hasGraphicContent = svg.querySelector('path, circle, ellipse, polygon, polyline, line, image');

    let finalSvgContent = svgContent;
    let contentWidth = workAreaWidth;
    let contentHeight = workAreaHeight;

    if (hasGraphicContent) {
        // Calculate actual bounding box of the SVG content
        const bounds = await calculateSvgBounds(svgContent);

        // Only crop if bounds are significantly smaller than viewBox (at least 10% smaller)
        const viewBoxArea = bounds.viewBoxWidth * bounds.viewBoxHeight;
        const contentArea = bounds.width * bounds.height;
        const shouldCrop = contentArea < viewBoxArea * 0.9 && bounds.width > 1 && bounds.height > 1;

        if (shouldCrop) {
            // Crop the SVG to actual content bounds
            finalSvgContent = cropSvgToContent(svgContent, bounds);
            contentWidth = bounds.width + 2; // +2 for padding
            contentHeight = bounds.height + 2;
        } else {
            contentWidth = bounds.viewBoxWidth;
            contentHeight = bounds.viewBoxHeight;
        }

        shapes.push({
            type: 'graphic',
            x: 0,
            y: 0,
            width: contentWidth,
            height: contentHeight,
            rotation: 0,
            orderIndex: orderIndex++,
            fieldId: undefined,
            svgData: finalSvgContent,
            score: false,
            engrave: false,
            cut: false,
        });
    }

    // Get viewBox offset for coordinate normalization
    let viewBoxX = 0, viewBoxY = 0;
    const viewBox = svg.getAttribute('viewBox');
    if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4) {
            viewBoxX = parts[0];
            viewBoxY = parts[1];
        }
    }

    // Find ungrouped rectangles (direct children of svg)
    const rects: Element[] = [];
    svg.querySelectorAll(':scope > rect').forEach(rect => {
        rects.push(rect);
    });

    // Calculate actual positions accounting for transform and viewBox offset
    interface RectData {
        actualX: number;
        actualY: number;
        width: number;
        height: number;
    }
    const rectData: RectData[] = [];

    rects.forEach(rect => {
        const transform = parseTransform(rect.getAttribute('transform'));
        const attrX = parseFloat(rect.getAttribute('x') || '0');
        const attrY = parseFloat(rect.getAttribute('y') || '0');
        const width = parseFloat(rect.getAttribute('width') || '0');
        const height = parseFloat(rect.getAttribute('height') || '0');

        if (width >= 1 && height >= 1) {
            // Actual position = attribute position + transform translation - viewBox offset
            const actualX = attrX + transform.tx - viewBoxX;
            const actualY = attrY + transform.ty - viewBoxY;
            rectData.push({ actualX, actualY, width, height });
        }
    });

    // Calculate offset to normalize positions (so minimum is at 0,0)
    let minX = Infinity, minY = Infinity;
    rectData.forEach(r => {
        minX = Math.min(minX, r.actualX);
        minY = Math.min(minY, r.actualY);
    });
    if (minX === Infinity) minX = 0;
    if (minY === Infinity) minY = 0;

    rectData.forEach(r => {
        shapes.push({
            type: 'rect',
            x: r.actualX - minX,
            y: r.actualY - minY,
            width: r.width,
            height: r.height,
            rotation: 0,
            orderIndex: orderIndex++,
            fieldId: undefined,
            score: false,
            engrave: false,
            cut: false,
        });
    });

    return {
        shapes,
        needsScaling: contentWidth > workAreaWidth || contentHeight > workAreaHeight,
        originalSize: { width: contentWidth, height: contentHeight },
        svgContent: finalSvgContent
    };
}

/**
 * Parse an SVG file and extract shapes (sync version - deprecated, use parseSvgFileAsync)
 */
export function parseSvgFile(
    svgContent: string,
    workAreaWidth: number,
    workAreaHeight: number
): ImportResult {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svg = doc.querySelector('svg');

    if (!svg) {
        throw new Error('Invalid SVG file');
    }

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
        throw new Error('Invalid SVG file: ' + parseError.textContent);
    }

    // Get SVG dimensions from viewBox or attributes
    let svgWidth = workAreaWidth;
    let svgHeight = workAreaHeight;

    const viewBox = svg.getAttribute('viewBox');
    if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4) {
            svgWidth = parts[2];
            svgHeight = parts[3];
        }
    } else {
        const widthAttr = svg.getAttribute('width');
        const heightAttr = svg.getAttribute('height');
        if (widthAttr) svgWidth = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || svgWidth;
        if (heightAttr) svgHeight = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || svgHeight;
    }

    const shapes: Omit<TemplateShape, 'id' | 'templateId'>[] = [];
    let orderIndex = 0;

    // Check if SVG has graphic content (paths, shapes, etc.)
    const hasGraphicContent = svg.querySelector('path, circle, ellipse, polygon, polyline, line, image');

    if (hasGraphicContent) {
        shapes.push({
            type: 'graphic',
            x: 0,
            y: 0,
            width: svgWidth,
            height: svgHeight,
            rotation: 0,
            orderIndex: orderIndex++,
            fieldId: undefined,
            svgData: svgContent,
            score: false,
            engrave: false,
            cut: false,
        });
    }

    // Get viewBox offset for coordinate normalization
    let viewBoxX = 0, viewBoxY = 0;
    if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number);
        if (parts.length === 4) {
            viewBoxX = parts[0];
            viewBoxY = parts[1];
        }
    }

    // Find ungrouped rectangles (direct children of svg)
    const rects: Element[] = [];
    svg.querySelectorAll(':scope > rect').forEach(rect => {
        rects.push(rect);
    });

    // Calculate actual positions accounting for transform and viewBox offset
    interface RectDataSync {
        actualX: number;
        actualY: number;
        width: number;
        height: number;
    }
    const rectData: RectDataSync[] = [];

    rects.forEach(rect => {
        const transform = parseTransform(rect.getAttribute('transform'));
        const attrX = parseFloat(rect.getAttribute('x') || '0');
        const attrY = parseFloat(rect.getAttribute('y') || '0');
        const width = parseFloat(rect.getAttribute('width') || '0');
        const height = parseFloat(rect.getAttribute('height') || '0');

        if (width >= 1 && height >= 1) {
            // Actual position = attribute position + transform translation - viewBox offset
            const actualX = attrX + transform.tx - viewBoxX;
            const actualY = attrY + transform.ty - viewBoxY;
            rectData.push({ actualX, actualY, width, height });
        }
    });

    // Calculate offset to normalize positions (so minimum is at 0,0)
    let minX = Infinity, minY = Infinity;
    rectData.forEach(r => {
        minX = Math.min(minX, r.actualX);
        minY = Math.min(minY, r.actualY);
    });
    if (minX === Infinity) minX = 0;
    if (minY === Infinity) minY = 0;

    rectData.forEach(r => {
        shapes.push({
            type: 'rect',
            x: r.actualX - minX,
            y: r.actualY - minY,
            width: r.width,
            height: r.height,
            rotation: 0,
            orderIndex: orderIndex++,
            fieldId: undefined,
            score: false,
            engrave: false,
            cut: false,
        });
    });

    return {
        shapes,
        needsScaling: svgWidth > workAreaWidth || svgHeight > workAreaHeight,
        originalSize: { width: svgWidth, height: svgHeight },
        svgContent
    };
}

/**
 * Scale shapes to fit work area
 */
export function scaleShapesToFit(
    shapes: Omit<TemplateShape, 'id' | 'templateId'>[],
    originalWidth: number,
    originalHeight: number,
    workAreaWidth: number,
    workAreaHeight: number
): Omit<TemplateShape, 'id' | 'templateId'>[] {
    const scaleX = workAreaWidth / originalWidth;
    const scaleY = workAreaHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY);

    return shapes.map(shape => ({
        ...shape,
        x: shape.x * scale,
        y: shape.y * scale,
        width: shape.width * scale,
        height: shape.height * scale
    }));
}

